import React, { useState } from 'react';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { testUserDataStructure, verifyUserDataIntegrity, getAllUsers, logExpectedStructure } from '../utils/firestoreTest';
import { testFirestoreConnection, createTestUser, checkFirestoreRules, runFirestoreDiagnostic } from '../utils/testFirestore';
import { logFirestoreRulesGuide } from '../utils/firestoreRulesGuide';
import { updateProfile } from 'firebase/auth';

const FirestoreDemo = () => {
  const { currentUser } = useAuth();
  const [testResults, setTestResults] = useState('');
  const [loading, setLoading] = useState(false);

  const runTest = async (testType) => {
    if (!currentUser) {
      setTestResults('❌ Please log in to test Firestore functionality');
      return;
    }

    setLoading(true);
    setTestResults('🔄 Running test...');

    try {
      let result = '';
      
      switch (testType) {
        case 'connection':
          console.clear();
          const connectionResult = await testFirestoreConnection();
          result = connectionResult.success 
            ? '✅ Firestore connection successful!' 
            : `❌ Connection failed: ${connectionResult.error}`;
          break;
          
        case 'rules':
          console.clear();
          const rulesResult = await checkFirestoreRules();
          result = rulesResult.success 
            ? '✅ Firestore rules allow operations!' 
            : `❌ Rules check failed: ${rulesResult.error}`;
          break;
          
        case 'testuser':
          console.clear();
          const testUserResult = await createTestUser();
          result = testUserResult.success 
            ? '✅ Test user created successfully!' 
            : `❌ Test user creation failed: ${testUserResult.error}`;
          break;
          
        case 'diagnostic':
          console.clear();
          await runFirestoreDiagnostic();
          result = 'Complete diagnostic run! Check console for detailed results.';
          break;
          
        case 'structure':
          console.clear();
          await testUserDataStructure(currentUser.uid);
          result = 'Test completed! Check browser console for detailed results.';
          break;
          
        case 'integrity':
          console.clear();
          const isValid = await verifyUserDataIntegrity(currentUser.uid);
          result = `Data integrity check: ${isValid ? '✅ PASSED' : '❌ FAILED'}\nCheck console for details.`;
          break;
          
        case 'allusers':
          console.clear();
          await getAllUsers();
          result = 'All users fetched! Check browser console for results.';
          break;
          
        case 'expected':
          console.clear();
          logExpectedStructure();
          result = 'Expected structure logged to console.';
          break;
          
        case 'rules-guide':
          console.clear();
          logFirestoreRulesGuide();
          result = 'Firestore rules guide logged to console! Check console for setup instructions.';
          break;
          
        case 'test-auth-methods':
          console.clear();
          console.log('🔍 Testing Firebase Auth user object methods...');
          console.log('Current user object:', currentUser);
          console.log('Has getIdToken method:', typeof currentUser?.getIdToken === 'function');
          console.log('Has updateProfile method available:', typeof updateProfile === 'function');
          console.log('User UID:', currentUser?.uid);
          console.log('User email:', currentUser?.email);
          console.log('User displayName:', currentUser?.displayName);
          console.log('Firestore data:', currentUser?.firestoreData);
          result = 'Auth methods test completed! Check console for results.';
          break;
          
        default:
          result = 'Unknown test type';
      }
      
      setTestResults(result);
    } catch (error) {
      setTestResults(`❌ Error running test: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DemoContainer>
      <Title>🔥 Firestore Connection & Data Testing</Title>
      
      {!currentUser && (
        <WarningBox>
          <WarningIcon>⚠️</WarningIcon>
          <WarningText>
            <strong>No user logged in</strong> - Some tests require authentication, but connection tests can run without login.
          </WarningText>
        </WarningBox>
      )}
      
      {currentUser && (
        <UserInfo>
          <strong>Current User:</strong> {currentUser.displayName || 'User'} ({currentUser.email})
        </UserInfo>
      )}
      
      <SectionTitle>🔧 Connection & Setup Tests</SectionTitle>
      <TestButtons>
        <TestButton onClick={() => runTest('connection')} disabled={loading}>
          Test Firestore Connection
        </TestButton>
        <TestButton onClick={() => runTest('rules')} disabled={loading}>
          Check Security Rules
        </TestButton>
        <TestButton onClick={() => runTest('testuser')} disabled={loading}>
          Create Test User
        </TestButton>
        <TestButton onClick={() => runTest('diagnostic')} disabled={loading}>
          Run Full Diagnostic
        </TestButton>
      </TestButtons>
      
      <SectionTitle>📋 Setup & Rules</SectionTitle>
      <TestButtons>
        <TestButton onClick={() => runTest('rules-guide')} disabled={loading}>
          Show Firestore Rules Guide
        </TestButton>
        <TestButton onClick={() => runTest('test-auth-methods')} disabled={loading}>
          Test Auth Methods
        </TestButton>
      </TestButtons>
      
      {currentUser && (
        <>
          <SectionTitle>📊 User Data Tests</SectionTitle>
          <TestButtons>
            <TestButton onClick={() => runTest('structure')} disabled={loading}>
              Test Data Structure
            </TestButton>
            <TestButton onClick={() => runTest('integrity')} disabled={loading}>
              Verify Data Integrity
            </TestButton>
            <TestButton onClick={() => runTest('allusers')} disabled={loading}>
              Get All Users
            </TestButton>
            <TestButton onClick={() => runTest('expected')} disabled={loading}>
              Show Expected Structure
            </TestButton>
          </TestButtons>
        </>
      )}
      
      {testResults && (
        <ResultsContainer>
          <ResultsTitle>Test Results:</ResultsTitle>
          <Results>{testResults}</Results>
          <ConsoleNote>💡 Detailed results are logged to the browser console (F12)</ConsoleNote>
        </ResultsContainer>
      )}
      
      <InfoSection>
        <InfoTitle>📋 Firestore Data Structure</InfoTitle>
        <InfoText>
          This demo tests the Firestore database integration. Each user document contains:
        </InfoText>
        <InfoList>
          <li><strong>Basic Info:</strong> uid, email, firstName, lastName, displayName</li>
          <li><strong>Contact:</strong> phone, dateOfBirth</li>
          <li><strong>Address:</strong> street, city, state, zipCode, country</li>
          <li><strong>Preferences:</strong> emailNotifications, smsNotifications, marketingCommunications</li>
          <li><strong>Profile:</strong> isComplete, lastUpdated</li>
          <li><strong>Timestamps:</strong> createdAt, lastLogin, updatedAt</li>
        </InfoList>
      </InfoSection>
    </DemoContainer>
  );
};

export default FirestoreDemo;

const DemoContainer = styled.div`
  max-width: 800px;
  margin: 40px auto;
  padding: 30px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  font-size: 28px;
  font-weight: 700;
  color: #111;
  margin-bottom: 20px;
  text-align: center;
`;

const UserInfo = styled.div`
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 30px;
  font-size: 16px;
  color: #333;
`;

const WarningBox = styled.div`
  background: #fff3cd;
  border: 2px solid #ffeaa7;
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 30px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const WarningIcon = styled.span`
  font-size: 24px;
`;

const WarningText = styled.div`
  color: #856404;
  font-size: 14px;
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #111;
  margin: 30px 0 20px 0;
  
  &:first-of-type {
    margin-top: 0;
  }
`;

const Message = styled.div`
  text-align: center;
  font-size: 18px;
  color: #666;
  padding: 40px 0;
`;

const TestButtons = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 15px;
  margin-bottom: 30px;
`;

const TestButton = styled.button`
  background: #111;
  color: white;
  border: none;
  padding: 15px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: #000;
    transform: translateY(-2px);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const ResultsContainer = styled.div`
  background: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
`;

const ResultsTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #111;
  margin-bottom: 15px;
`;

const Results = styled.pre`
  background: #fff;
  padding: 15px;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  white-space: pre-wrap;
  word-break: break-word;
  color: #333;
  border: 1px solid #dee2e6;
`;

const ConsoleNote = styled.div`
  font-size: 12px;
  color: #666;
  font-style: italic;
  margin-top: 10px;
`;

const InfoSection = styled.div`
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  padding: 25px;
  border-radius: 12px;
  border: 1px solid #e1bee7;
`;

const InfoTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #111;
  margin-bottom: 15px;
`;

const InfoText = styled.p`
  font-size: 16px;
  color: #333;
  margin-bottom: 15px;
  line-height: 1.5;
`;

const InfoList = styled.ul`
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  
  li {
    margin-bottom: 8px;
    
    strong {
      color: #111;
    }
  }
`;
